import { useEffect, useRef, useState } from "react";
import { getCsrfHeader } from "../../../lib/csrf";
import { extractAppId, extractClientSecret, extractMsTenantId } from "./msteamsSetup";
import styles from "./MsTeamsSetupModal.module.css";

type Props = {
  open: boolean;
  // The tenant whose OpenClaw we wire Teams onto.
  tenantId: string;
  // Integration title for copy (e.g. "Microsoft Teams").
  title: string;
  onClose: () => void;
  // Called after the connection state changes (credentials wired / paired) so
  // the page can refetch and reflect the new status.
  onChanged: () => void;
};

// Step model. Unlike Telegram there is no paste-the-code pairing step: the
// msteams channel holds a stranger's DM silently and never sends the code
// back, so the "approve" step polls the pending-requests endpoint and offers
// "that's me — approve" instead.
type StepKey = "create" | "credentials" | "endpoint" | "teamsapp" | "approve" | "done";

const STEPS: ReadonlyArray<{ key: StepKey; label: string }> = [
  { key: "create", label: "Create bot" },
  { key: "credentials", label: "Add credentials" },
  { key: "endpoint", label: "Set endpoint" },
  { key: "teamsapp", label: "Add to Teams" },
  { key: "approve", label: "Approve yourself" },
  { key: "done", label: "Done" },
];

// Per-step GIF + caption; assets live in public/integrations/msteams/ and are
// served at /integrations/msteams/<file>. A null entry (the done step) drops
// the media pane entirely and the step body takes the full width (approve
// and done); a load
// failure hides the pane rather than showing a dead placeholder.
const STEP_MEDIA: Record<StepKey, { src: string | null; caption: string } | null> = {
  create: { src: "/integrations/msteams/step-create.gif", caption: "Creating the bot in Teams Developer Portal" },
  credentials: { src: "/integrations/msteams/step-credentials.gif", caption: "Copying the app ID, secret, and tenant ID" },
  endpoint: { src: "/integrations/msteams/step-endpoint.gif", caption: "Pasting the webhook URL as the bot endpoint" },
  teamsapp: { src: "/integrations/msteams/step-teamsapp.gif", caption: "Creating the Teams app and previewing it in Teams" },
  approve: null,
  done: null,
};

const StepMedia = ({ step }: { step: StepKey }): JSX.Element | null => {
  const media = STEP_MEDIA[step];
  const [errored, setErrored] = useState<boolean>(false);
  useEffect(() => {
    setErrored(false);
  }, [step]);
  if (media === null || media.src === null || errored) return null;
  return (
    <div className={styles.media}>
      <img className={styles.mediaImg} src={media.src} alt={media.caption} onError={() => setErrored(true)} />
    </div>
  );
};

type PairingRequest = { code: string; name: string | null; requestedAt: string | null };

// Root, not /bots: Microsoft's "New Developer Portal" 404s the old /bots
// path — bot management now lives under Tools in the left nav.
const DEV_PORTAL_URL = "https://dev.teams.microsoft.com";

// Guided, self-serve Microsoft Teams setup. Walks the customer (or their M365
// admin) through creating a bot in THEIR tenant — no Azure subscription
// needed — pasting its credentials (ztc-core pushes them onto their
// OpenClaw), registering our relay URL as the bot's messaging endpoint, and
// approving themselves from the live pending-requests list. Native <dialog>
// + showModal(), matching TelegramSetupModal.
export const MsTeamsSetupModal = ({ open, tenantId, title, onClose, onChanged }: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [appId, setAppId] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string>("");
  const [msTenantId, setMsTenantId] = useState<string>("");
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [requests, setRequests] = useState<ReadonlyArray<PairingRequest>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // The pairing code currently being approved, or null when none is in flight.
  // Keyed to the row so approving one held sender doesn't relabel every row.
  const [approvingCode, setApprovingCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  // Clipboard access denied (permissions / non-secure context): reveal the
  // raw URL as the fallback so the customer is never stuck.
  const [copyFailed, setCopyFailed] = useState<boolean>(false);
  // Background box-wiring progress from GET /status ("applying"/"ok"/
  // "failed"). The credentials call returns instantly; the slow SSM wiring
  // continues server-side and this is how the wizard tracks it.
  const [applyState, setApplyState] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setStepIndex(0);
      setAppId("");
      setClientSecret("");
      setMsTenantId("");
      setWebhookUrl(null);
      setRequests([]);
      setSubmitting(false);
      setApprovingCode(null);
      setError(null);
      setCopied(false);
      setCopyFailed(false);
      setApplyState(null);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  const step: StepKey = STEPS[stepIndex]?.key ?? "create";

  // Poll pending pairing requests while the approve step is showing. Each
  // poll shells into the customer's instance, so keep the cadence gentle.
  useEffect(() => {
    if (!open || step !== "approve") return;
    let cancelled = false;
    let timer: number | undefined;
    const load = async (): Promise<void> => {
      try {
        const res = await fetch(
          `/api/integrations/${encodeURIComponent(tenantId)}/msteams/pairing-requests`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const data = (await res.json()) as { requests: PairingRequest[] };
        if (!cancelled) setRequests(data.requests);
      } catch {
        // Transient poll failure: keep the last list; the next poll retries.
      } finally {
        // Re-arm off completion, not on a fixed interval: each poll shells an
        // SSM Run Command into the customer's box, so we keep them strictly
        // one-at-a-time — a slow backend just delays the next tick instead of
        // stacking overlapping in-flight commands.
        if (!cancelled) timer = window.setTimeout(() => void load(), 5000);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [open, step, tenantId]);

  // Track the background apply while the customer works through the
  // Microsoft-side steps. Stops polling once it lands on ok/failed.
  useEffect(() => {
    if (!open || stepIndex < 2 || applyState === "ok" || applyState === "failed") return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await fetch(`/api/integrations/${encodeURIComponent(tenantId)}/msteams/status`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = (await res.json()) as { applyState: string | null };
        if (!cancelled && data.applyState !== null) setApplyState(data.applyState);
      } catch {
        // Transient poll failure: the next tick retries.
      }
    };
    void load();
    const id = window.setInterval(() => void load(), 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [open, stepIndex, applyState, tenantId]);

  if (!open) return null;

  // Paste-tolerant: pull each value out of whatever the customer pasted
  // (often the whole teams-cli credentials block). null = nothing valid yet.
  const appIdCandidate = extractAppId(appId);
  const secretCandidate = extractClientSecret(clientSecret);
  const msTenantCandidate = extractMsTenantId(msTenantId);
  const credentialsReady = appIdCandidate !== null && secretCandidate !== null && msTenantCandidate !== null;

  const close = (): void => {
    // Refetch on close so the page reflects partial progress (credentials
    // wired but not paired -> "pending").
    onChanged();
    onClose();
  };

  const submitCredentials = async (): Promise<void> => {
    if (!credentialsReady) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/integrations/${encodeURIComponent(tenantId)}/msteams/credentials`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({
          appId: appIdCandidate,
          appPassword: secretCandidate,
          msTenantId: msTenantCandidate,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Setup failed (${res.status})`);
      }
      const data = (await res.json()) as { webhookUrl: string };
      setWebhookUrl(data.webhookUrl);
      setApplyState("applying");
      onChanged();
      setStepIndex(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const approve = async (code: string): Promise<void> => {
    setApprovingCode(code);
    setError(null);
    try {
      const res = await fetch(`/api/integrations/${encodeURIComponent(tenantId)}/msteams/pair`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ pairingCode: code }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Approval failed (${res.status})`);
      }
      const data = (await res.json()) as { status: string };
      if (data.status === "approved") {
        onChanged();
        setStepIndex(5);
      } else {
        // The request vanished between poll and click (expired / already
        // approved elsewhere). Drop it; the next poll refreshes the list.
        setRequests((prev) => prev.filter((r) => r.code !== code));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setApprovingCode(null);
    }
  };

  const copyWebhookUrl = async (): Promise<void> => {
    if (webhookUrl === null) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting && approvingCode === null) close();
      }}
    >
      <div className={styles.shell}>
        <header className={styles.header}>
          <h2 className={styles.title}>Set up {title}</h2>
          <ol className={styles.steps}>
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={`${styles.stepDot} ${
                  i === stepIndex ? styles.stepDotActive : i < stepIndex ? styles.stepDotDone : ""
                }`}
              >
                <span className={styles.stepNum}>{i < stepIndex ? "✓" : i + 1}</span>
                <span className={styles.stepLabel}>{s.label}</span>
              </li>
            ))}
          </ol>
        </header>

        <div className={STEP_MEDIA[step] === null ? styles.splitSingle : styles.split}>
          <div className={styles.body}>
            {step === "create" ? (
              <>
                <p className={styles.lead}>
                  Your agent gets its own bot inside <strong>your</strong> Microsoft 365. You'll want whoever admins
                  your Microsoft 365 for this.
                </p>
                <ol className={styles.stepList}>
                  <li className={styles.stepItem}>
                    Open the <strong>Teams Developer Portal</strong> and sign in with your work account.
                    <div className={styles.inlineAction}>
                      <a className={styles.actionBtn} href={DEV_PORTAL_URL} target="_blank" rel="noreferrer noopener">
                        Open Developer Portal<span aria-hidden> →</span>
                      </a>
                    </div>
                  </li>
                  <li className={styles.stepItem}>
                    Under <strong>Tools → Bot management</strong>, create a <strong>New Bot</strong> named after your
                    agent. One catch: Microsoft rejects names containing “Teams” or “Microsoft”.
                  </li>
                  <li className={styles.stepItem}>
                    On the new bot, open <strong>Client secrets</strong> and generate one — copy the{" "}
                    <strong>secret value</strong> right away (it's only shown once). Your <strong>bot ID</strong> is
                    the long code shown next to the bot's name back in the Bot management list.
                  </li>
                  <li className={styles.stepItem}>
                    You'll also need your <strong>Directory (tenant) ID</strong>: in the Entra admin centre it's on
                    the <strong>Overview</strong> page under <strong>Basic information</strong>, with a copy button
                    next to it.
                    <div className={styles.inlineAction}>
                      <a
                        className={styles.actionBtn}
                        href="https://entra.microsoft.com"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Open Entra admin centre<span aria-hidden> →</span>
                      </a>
                    </div>
                  </li>
                </ol>
              </>
            ) : null}

            {step === "credentials" ? (
              <>
                <p className={styles.lead}>
                  Paste the bot's credentials. We'll wire them onto your agent — this takes a minute or two while it
                  restarts.
                </p>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Bot / application (client) ID — next to your bot's name in Bot management</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={appId}
                    onChange={(e) => {
                      setAppId(e.target.value);
                      setError(null);
                    }}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={submitting}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Client secret — the value you generated under Client secrets</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={clientSecret}
                    onChange={(e) => {
                      setClientSecret(e.target.value);
                      setError(null);
                    }}
                    placeholder="The secret value you just generated"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={submitting}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Directory (tenant) ID — Entra admin centre → Overview → Basic information</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={msTenantId}
                    onChange={(e) => {
                      setMsTenantId(e.target.value);
                      setError(null);
                    }}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={submitting}
                  />
                </label>
                {submitting ? (
                  <p className={styles.hint}>Connecting… this can take a couple of minutes while your agent restarts.</p>
                ) : appId.trim() !== "" && appIdCandidate === null ? (
                  <p className={styles.hintWarn}>The bot ID should be a GUID like <code className={styles.code}>5d2d08f9-…</code>. You can paste the whole credentials block; we'll find it.</p>
                ) : clientSecret.trim() !== "" && secretCandidate === null ? (
                  <p className={styles.hintWarn}>That looks like an ID, not a secret — the secret is the longer value you generated under Client secrets.</p>
                ) : msTenantId.trim() !== "" && msTenantCandidate === null ? (
                  <p className={styles.hintWarn}>The tenant ID should be a GUID. It's on the Entra admin centre Overview page.</p>
                ) : null}
              </>
            ) : null}

            {step === "endpoint" ? (
              <>
                <p className={styles.lead}>
                  Your agent is listening. Now tell Microsoft where to deliver its messages.
                </p>
                <ol className={styles.stepList}>
                  <li className={styles.stepItem}>
                    Back in the Developer Portal, open <strong>Tools → Bot management</strong> and select your bot.
                  </li>
                  <li className={styles.stepItem}>
                    Open <strong>Configure</strong> and paste your unique endpoint address into{" "}
                    <strong>Bot endpoint address</strong>, then save:
                    <div className={styles.inlineAction}>
                      <button type="button" className={styles.actionBtn} onClick={() => void copyWebhookUrl()}>
                        {copied ? "Copied to clipboard ✓" : "Copy endpoint address"}
                      </button>
                    </div>
                    {copyFailed ? (
                      <p className={styles.hintWarn}>
                        Your browser blocked the clipboard — copy it manually:{" "}
                        <code className={styles.code}>{webhookUrl}</code>
                      </p>
                    ) : null}
                  </li>
                </ol>
              </>
            ) : null}

            {step === "teamsapp" ? (
              <>
                <p className={styles.lead}>
                  Last Microsoft step: wrap the bot in a Teams app so it shows up in your Teams.
                </p>
                <ol className={styles.stepList}>
                  <li className={styles.stepItem}>
                    In the Developer Portal, go to <strong>Apps → New app</strong> and give it your agent's name.
                  </li>
                  <li className={styles.stepItem}>
                    Under <strong>Basic information</strong>, Microsoft requires all of these before the app will
                    install — fill each one, then save:
                    <ul className={styles.stepSubList}>
                      <li><strong>Short description</strong> and <strong>Long description</strong> — one line each is fine.</li>
                      <li><strong>Developer (company) name</strong> — your business name.</li>
                      <li>
                        <strong>Website</strong>, <strong>Privacy policy</strong> and <strong>Terms of use</strong> —
                        real <code className={styles.code}>https</code> links; use your own website and, if you have
                        them, your privacy and terms pages (your website works for all three if not).
                      </li>
                    </ul>
                  </li>
                  <li className={styles.stepItem}>
                    <strong>The step that makes it a bot — don't skip it:</strong> under{" "}
                    <strong>App features</strong>, choose <strong>Bot</strong>, pick your bot from the{" "}
                    <strong>Select an existing bot</strong> list (or paste the bot ID), tick{" "}
                    <strong>Personal</strong> under scopes, and save. Without this the app installs as an empty
                    shell and Teams shows "not available".
                  </li>
                  <li className={styles.stepItem}>
                    Hit <strong>Preview in Teams</strong> (top right) and <strong>Add</strong> the app. If Teams
                    refuses, an admin needs to switch on <strong>Upload custom apps</strong> (Teams admin centre →
                    Teams apps → Setup policies → Global). Want teammates to use it too? Choose{" "}
                    <strong>Publish to your org</strong> instead — then an admin approves it once under{" "}
                    <strong>Teams admin centre → Teams apps → Manage apps</strong> (open the app, set its publishing
                    status to Publish) and everyone installs it from <strong>Apps → Built for your org</strong>.
                  </li>
                  <li className={styles.stepItem}>
                    <strong>Send the bot any message</strong> — even just “hi”. It won't reply yet; that's expected.
                  </li>
                </ol>
              </>
            ) : null}

            {step === "approve" ? (
              <>
                <p className={styles.lead}>
                  For security, your agent ignores strangers. Message the bot in Teams, then approve yourself here.
                </p>
                {requests.length === 0 ? (
                  <p className={styles.hint}>
                    Waiting for your message… we check every few seconds. Make sure you've sent the bot a DM in
                    Teams.
                  </p>
                ) : (
                  <ul className={styles.requestList}>
                    {requests.map((r) => (
                      <li key={r.code} className={styles.requestRow}>
                        <span className={styles.requestName}>{r.name ?? "Someone in your Teams"}</span>
                        <button
                          type="button"
                          className={styles.primaryBtn}
                          onClick={() => void approve(r.code)}
                          disabled={approvingCode !== null}
                        >
                          {approvingCode === r.code ? "Approving…" : "That's me — approve"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className={styles.hint}>
                  Only approve people you recognise — approval gives that person a direct line to your agent.
                </p>
                {applyState === "applying" ? (
                  <p className={styles.hint}>
                    Your agent is still being wired up in the background — that finishes on its own within a couple
                    of minutes.
                  </p>
                ) : null}
              </>
            ) : null}

            {step === "done" ? (
              <>
                <div className={styles.successMark} aria-hidden>✓</div>
                <p className={styles.lead}>
                  <strong>You're connected.</strong> Send the bot a message in Teams and your agent will reply right
                  there.
                </p>
                <p className={styles.hint}>
                  Teammates can be added the same way: they message the bot, you approve them from this screen.
                </p>
              </>
            ) : null}

            {applyState === "failed" && step !== "done" ? (
              <div className={styles.errorBanner}>
                We couldn't finish wiring your agent. Go back to the credentials step and press Connect bot again.
              </div>
            ) : null}
            {error ? <div className={styles.errorBanner}>{error}</div> : null}
          </div>
          {STEP_MEDIA[step] !== null ? (
            <div className={styles.mediaCol}>
              <StepMedia step={step} />
            </div>
          ) : null}
        </div>

        <footer className={styles.footer}>
          {step === "create" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={close}>Cancel</button>
              <button type="button" className={styles.primaryBtn} onClick={() => setStepIndex(1)}>
                I've created my bot
              </button>
            </>
          ) : null}

          {step === "credentials" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setStepIndex(0)} disabled={submitting}>
                Back
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => void submitCredentials()}
                disabled={submitting || !credentialsReady}
              >
                {submitting ? "Connecting…" : "Connect bot"}
              </button>
            </>
          ) : null}

          {step === "endpoint" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setStepIndex(1)}>Back</button>
              <button type="button" className={styles.primaryBtn} onClick={() => setStepIndex(3)}>
                I've set the endpoint
              </button>
            </>
          ) : null}

          {step === "teamsapp" ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setStepIndex(2)}>Back</button>
              <button type="button" className={styles.primaryBtn} onClick={() => setStepIndex(4)}>
                I've messaged the bot
              </button>
            </>
          ) : null}

          {step === "approve" ? (
            <button type="button" className={styles.secondaryBtn} onClick={() => setStepIndex(3)} disabled={approvingCode !== null}>
              Back
            </button>
          ) : null}

          {step === "done" ? (
            <button type="button" className={styles.primaryBtn} onClick={close}>Done</button>
          ) : null}
        </footer>
      </div>
    </dialog>
  );
};
